import {
	grantEsignRegex,
	noaRegex,
	optOutRegex,
	paymentPlanRegex,
	secureCodeRegex,
	transactionalEmailOptOutRegex,
} from "./routeExtractionRegexs"

import {isRegionNumber, RegionNumber} from "@indebted/api/regionManager"

/**
 * Extract region from various route patterns
 * Handles multiple URL patterns:
 * - Subdomain routes: my.au.indebted.co, my.br.indebted.co, etc. (region from subdomain)
 * - Secure code routes: /:secureCode/* (region from first character of secure code)
 * - Payment plan routes: /0/payment-plan/*, /1/payment-plan/*, /2/payment-plan/* (region number directly in URL)
 * - Payment arrangement routes: /:region/payment-arrangement/* (region as URL parameter)
 * - Grant ESIGN routes: /customers/:regionPrefix/grant-esign-consent/* (region prefix in URL)
 * - Opt-out routes: /opt-out/:token/:channel/* (region from AWS region query param)
 * - Transactional email opt-out: /transactional-email-opt-out/:token (region from AWS region query param)
 * - NOA acknowledgement: /accounts/ack-noa (region from AWS region query param)
 */
const regionFromRouteExtractor = (
	pathname: string,
	searchParams?: URLSearchParams,
	hostname?: string,
): RegionNumber | undefined => {
	// Subdomain routes: my.au.indebted.co, my.br.indebted.co, etc.
	if (hostname) {
		const subdomainMatch = hostname.match(/^my\.([a-z]{2})\.indebted\.co$/i)
		if (subdomainMatch) {
			const regionCode = subdomainMatch[1].toLowerCase()
			// Map region codes to region numbers
			if (regionCode === "au") return 0 // Australia
			if (regionCode === "us") return 1 // United States
			if (regionCode === "ae") return 2 // United Arab Emirates
			if (regionCode === "br") return 3 // Brazil

			console.error(`[regionFromRouteExtractor] Unknown region code "${regionCode}" in subdomain: ${hostname}`)
			return undefined
		}
	}

	// Payment plan routes with hardcoded region numbers: /0/payment-plan/*, /1/payment-plan/*, /2/payment-plan/*
	const paymentPlanMatch = pathname.match(paymentPlanRegex)
	if (paymentPlanMatch) {
		const regionNumber = parseInt(paymentPlanMatch[1], 10)
		// Validate that it's a valid region number (0=AU, 1=US, 2=AE)
		if (isRegionNumber(regionNumber)) {
			return regionNumber
		}
		console.error(
			`[regionFromRouteExtractor] Invalid region number "${paymentPlanMatch[1]}" in payment plan route: ${pathname}`,
		)
		return undefined
	}

	// Payment arrangement routes: /:region/payment-arrangement/*
	const paymentArrangementRegex = /^\/([^/]+)\/payment-arrangement\/[^/]+(?:\/.*)?$/
	const paymentArrangementMatch = pathname.match(paymentArrangementRegex)
	if (paymentArrangementMatch) {
		const regionParam = paymentArrangementMatch[1]
		// Convert region string to region number if it's a valid region
		const regionNumber = parseInt(regionParam, 10)
		if (isRegionNumber(regionNumber)) {
			return regionNumber
		}
		console.error(
			`[regionFromRouteExtractor] Unknown region "${regionParam}" in payment arrangement route: ${pathname}`,
		)
		return undefined
	}

	// Grant ESIGN consent routes: /customers/:regionPrefix/grant-esign-consent/*
	const grantEsignMatch = pathname.match(grantEsignRegex)
	if (grantEsignMatch) {
		const regionNumber = parseInt(grantEsignMatch[1], 10)
		// Validate that it's a valid region number (0=AU, 1=US, 2=AE)
		if (isRegionNumber(regionNumber)) {
			return regionNumber
		}
		console.error(
			`[regionFromRouteExtractor] Invalid region number "${grantEsignMatch[1]}" in grant ESIGN route: ${pathname}`,
		)
		return undefined
	}

	// Secure code routes: /:secureCode/* (X-XXXX-XXXX or XXXX-XXXX pattern)
	const secureCodeMatch = pathname.match(secureCodeRegex)
	if (secureCodeMatch) {
		const secureCode = secureCodeMatch[1]

		// Validate secure code length (must be 9 or 11 characters)
		if (secureCode.length !== 9 && secureCode.length !== 11) {
			console.error(
				`[regionFromRouteExtractor] Invalid secure code length ${secureCode.length} (expected 9 or 11): ${secureCode} in route: ${pathname}`,
			)
			return undefined
		}

		// For 11-character secure codes (X-XXXX-XXXX), region determined by first character
		if (secureCode.length === 11) {
			const regionNumber = parseInt(secureCode[0], 10)
			if (isRegionNumber(regionNumber)) {
				return regionNumber
			} else {
				console.error(
					`[regionFromRouteExtractor] Unknown region identifier "${secureCode[0]}" in 11-character secure code: ${secureCode} in route: ${pathname}`,
				)
				return undefined
			}
		}

		// For 9-character secure codes (XXXX-XXXX), always return AU region (default)
		if (secureCode.length === 9) {
			return 0 // AU region (default for 9-character codes)
		}
	}

	// Routes that use AWS region from query parameters
	const awsRegionRoutes = [optOutRegex, transactionalEmailOptOutRegex, noaRegex]

	const isAwsRegionRoute = awsRegionRoutes.some((regex) => regex.test(pathname))
	if (isAwsRegionRoute && searchParams) {
		const awsRegion = searchParams.get("region")
		if (awsRegion !== null) {
			// Convert AWS region to region number
			if (awsRegion === "us-west-2") return 1 // US
			if (awsRegion === "me-central-1") return 2 // AE
			if (awsRegion === "sa-east-1") return 3 // BR
			return 0 // AU (default for ap-southeast-2, empty string, or any other)
		}
	} else if (isAwsRegionRoute && !searchParams) {
		console.error(
			`[regionFromRouteExtractor] AWS region route detected but no search parameters provided: ${pathname}`,
		)
		return undefined
	}

	console.error(`[regionFromRouteExtractor] No matching route pattern found for: ${pathname}`)
	return undefined
}

export {regionFromRouteExtractor}
